import * as React from 'react'
import './sidebar.css'
import { ReactSortable } from 'react-sortablejs'
import { nevoLevelNames, nevoPropertyNames } from '../state/nevo'
import { AtiLayer, CropMapLayer, DatasetLayer, GeoserverLayer, IMDLayer, KewLayer, KewPointLayer, KewShapeLayer, Layer, ModelOutputLayer, NevoLayer, OverlayLayer, PaintbrushGeoJSONLayer, ShapeLayer, State, WFSLayer } from '../state'
import { iconForLayerType, shortLabelForLayerType } from "../utils/util"
import { getColorStops } from '../reify_layer/model_output'
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid, TileGridProps, formatTileGridUnitLabel, tileGridStats } from '../modelling/tile_grid'
import { IMDProperties } from '../reify_layer/imd'
import { KewPointOptions, KewTreeCrownOptions } from '../reify_layer/kew'
import { natmap_outputs } from '../modelling/components/natmap_soil_component'
import distinctColors from "distinct-colors"
import { PaintbrushColorPicker } from '../../paintbrush/paintbrush_color_picker'
import { LayerListRow } from "../../shared/layer_list_row"
import { slotToKey, keyToSlot, type MapStackSlot } from "../map/map_layer_stack"
import type { LocalGeotiffLayerState } from "../map/local_geotiff_preview"
import { geoserverCategoricalEntries } from "../layers/geoserver_categorical"
import { ChartDetailExpandedDialog, ModelDetailPanel } from "./model_detail_panel"
import {
  availableTableColumnGroupKeys,
  categoryGroupKeyForLabel,
  getCategoricalToNumericDetailEntries,
  getChartDetailEntry,
} from "./model_detail"
import { useExpandedChartId } from "../snapshot/chart_detail_expand"
import {
  CATEGORICAL_VIEW_CLASSES,
  effectiveLegendStatsForLayer,
  getCategoricalTableRowViewOptions,
  getCategoricalTableRowViewOptionsForTable,
  getLinkedTablesForLayer,
  isCategoricalTableRowView,
  parseTableRowView,
  resolveTableRowMappingForLayer,
} from "../layers/categorical_layer_view"
import type { ModelEditorApi } from "./model_editor_api"
import { AegisMapTab, useAegisSession } from "../aegis/session"
import { useMapOutputHighlightSnapshot } from "./map_output_highlight"
import { Data } from "rete/types/core/data"
import {
  buildModelDatasetStyleCopyEntries,
  CopyLayerStyleControl,
  isModelDatasetLayer,
  isTileGridLoaded,
  type ModelDatasetStyleCopyEntry,
} from "../layers/layer_style"
import {
  applyCategoricalColorMutation,
  labelEntriesFromStatsLabels,
  mergeCategoricalStyleOnCopy,
  resolveCategoricalPalette,
} from "../layers/categorical_layer_colors"
import {
  zoomResolutionTierBackground,
  zoomResolutionTierLabel,
} from "../map/zoom_resolution_tier"

export const colMapList = <>
  <optgroup label="Greyscale"></optgroup>
  <option value="greyscale">Greyscale</option>
  <optgroup label="Heatmap"></optgroup>
  <option value="jet">Jet</option>
  <option value="hsv">HSV</option>
  <option value="hot">Hot</option>
  <option value="cool">Cool</option>
  <option value="spring">Spring</option>
  <option value="summer">Summer</option>
  <option value="autumn">Autumn</option>
  <option value="winter">Winter</option>
  <option value="copper">Copper</option>
  <option value="YIGnBu">YIGnBu</option>
  <option value="greens">Greens</option>
  <option value="YIOrRd">YIOrRd</option>
  <option value="bluered">Bluered</option>
  <option value="RdBu">RdBu</option>
  <option value="BuRd">BuRd</option>
  <option value="picnic">Picnic</option>
  <option value="rainbow">Rainbow</option>
  <option value="portland">Portland</option>
  <option value="blackbody">Blackbody</option>
  <option value="earth">Earth</option>
  <option value="electric">Electric</option>
  <option value="viridis">Viridis</option>
  <option value="inferno">Inferno</option>
  <option value="magma">Magma</option>
  <option value="plasma">Plasma</option>
  <option value="warm">Warm</option>
  <option value="rainbow-soft">Rainbow-soft</option>
  <option value="bathymetry">Bathymetry</option>
  <option value="cdom">Cdom</option>
  <option value="chlorophyll">Chlorophyll</option>
  <option value="density">Density</option>
  <option value="freesurface-blue">Freesurface-Blue</option>
  <option value="freesurface-red">Freesurface-Red</option>
  <option value="oxygen">Oxygen</option>
  <option value="par">Par</option>
  <option value="phase">Phase</option>
  <option value="salinity">Salinity</option>
  <option value="temperature">Temperature</option>
  <option value="turbidity">Turbidity</option>
  <option value="velocity-blue">Velocity-Blue</option>
  <option value="velocity-green">Velocity-Green</option>
  <option value="cubehelix">Cubehelix</option>
</>

interface OverlayLayerSettingsProps {
  layer: OverlayLayer
  mutate: (data: any) => void
}
export const OverlayLayerSettings = ({ layer, mutate }: OverlayLayerSettingsProps) => (
  <>
    <div className="d-flex align-items-center mt-3">
      Stroke width
      <input
        type="range"
        min="1"
        max="10"
        step="1"
        className="custom-range ml-3"
        value={layer.strokeWidth}
        onChange={e => mutate({ strokeWidth: Number(e.target.value) })}
      />
    </div>
    <div className="d-flex align-items-center mt-3">
      Fill opacity
      <input
        type="range"
        min="0"
        max="1"
        step="0.1"
        className="custom-range ml-3"
        value={layer.fillOpacity}
        onChange={e => mutate({ fillOpacity: Number(e.target.value) })}
      />
    </div>
  </>
)

interface WFSLayerSettingsProps {
  layer: WFSLayer
  mutate: (data: any) => void
}

export const WFSLayerSettings = ({ layer, mutate }: WFSLayerSettingsProps) => (
  <>
    <div className="d-flex align-items-center mt-3">
      Fill mode
      <select className="custom-select ml-3" value={layer.fill} onChange={e => mutate({ fill: e.target.value })}>
        {colMapList}
      </select>
    </div>
      <div className="d-flex align-items-center mt-3">
      Property
      <select className="custom-select ml-3" value={layer.propIdx} onChange={e => mutate({ propIdx: e.target.value })}>
        {
          natmap_outputs.map((val, index) => {
            return <option key={index} value={index}>{val.name}</option>
          })
        }
      </select>
    </div>
  </>
)

interface GeoserverLayerSettingsProps {
  layer: GeoserverLayer
  mutate: (data: any) => void
}

export const GeoserverLayerSettings = ({ layer, mutate }: GeoserverLayerSettingsProps) => (
  <>
    {
      layer.fill && !layer.categoricalRaster &&
      <div className="d-flex align-items-center mt-3">
        Fill mode
        <select className="custom-select ml-3" value={layer.fill} onChange={e => mutate({ fill: e.target.value })}>
          {colMapList}
        </select>
      </div>
    }
  </>
)

interface NevoLayerSettingsProps {
  layer: NevoLayer
  mutate: (data: any) => void
}
export const NevoLayerSettings = ({ layer, mutate }: NevoLayerSettingsProps) => (
  <>
    <div className="d-flex align-items-center mt-3">
      Scale
      <select className="custom-select ml-3" value={layer.level} onChange={e => mutate({ level: e.target.value })}>
        {
          Object.keys(nevoLevelNames).map(level =>
            <option key={level} value={level}>{nevoLevelNames[level]}</option>
          )
        }
      </select>
    </div>
    <div className="d-flex align-items-center mt-3">
      Fill mode
      <select className="custom-select ml-3" value={layer.fill} onChange={e => mutate({ fill: e.target.value })}>
        <option value="heatmap">Heatmap</option>
        <option value="greyscale">Greyscale</option>
      </select>
    </div>
    <div className="d-flex flex-column mt-3">
      Visualised property
      <select className="custom-select" value={layer.property} onChange={e => mutate({ property: e.target.value })}>
        {
          Object.keys(nevoPropertyNames).map(property =>
            <option key={property} value={property}>
              {property} - {nevoPropertyNames[property]}
            </option>
          )
        }
      </select>
    </div>
  </>
)

interface ATILayerSettingsProps {
  layer: AtiLayer
}

export const ATILayerSettings = ({ layer }: ATILayerSettingsProps) => {

  const colors = layer.colors
  // backwards compatibility for old layers
  const notable = colors.notable ?? [158, 52, 235, 1]

  return (
    <details className="mt-3">
      <summary>Legend</summary>
      <span className="swatch" style={{ backgroundColor: `rgb(${colors.ancient[0]},${colors.ancient[1]},${colors.ancient[2]},1)` }} /> Ancient Tree<br />
      <span className="swatch" style={{ backgroundColor: `rgb(${colors.veteran[0]},${colors.veteran[1]},${colors.veteran[2]},1)` }} /> Veteran Tree<br />
      <span className="swatch" style={{ backgroundColor: `rgb(${notable[0]},${notable[1]},${notable[2]},1)` }} /> Notable Tree<br />
      <br />
      <span className="swatch" style={{ backgroundColor: `rgb(${colors.public[0]},${colors.public[1]},${colors.public[2]},1)` }} /> Public<br />
      <span className="swatch" style={{ backgroundColor: `rgb(${colors.private[0]},${colors.private[1]},${colors.private[2]},1)` }} /> Private<br />
    </details>
  )

}

interface ShapeLayerSettingsProps {
  layer: ShapeLayer
  mutate: (data: any) => void
}

function getDefaultShapeLayerValueColors(count: number, opacity: number): Array<[number, number, number, number]> {
  return distinctColors({ count: Math.max(count, 1) }).map(color => {
    const rgba = color.rgba()
    return [rgba[0], rgba[1], rgba[2], opacity]
  }) as Array<[number, number, number, number]>
}

function colorForShapeLayerValue(layer: ShapeLayer, value: string, index: number): string {
  const configured = selectedValueColors(layer)[index]
  if (configured) {
    return `rgba(${configured[0]}, ${configured[1]}, ${configured[2]}, ${configured[3]})`
  }

  const color = getDefaultShapeLayerValueColors(index + 1, layer.opacity)[index]
  return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`
}

function selectedValueColors(layer: ShapeLayer): Array<[number, number, number, number]> {
  if (!layer.selectedField) return []
  return layer.valueColors?.[layer.selectedField] || []
}

function getDefaultRasterCategoryColors(count: number): Array<[number, number, number, number]> {
  return distinctColors({ count: Math.max(count, 1) }).map(color => {
    const rgba = color.rgba()
    return [rgba[0], rgba[1], rgba[2], 1]
  }) as Array<[number, number, number, number]>
}

function selectedRasterValueColors(layer: GeoserverLayer): Array<[number, number, number, number]> {
  return layer.valueColors || []
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16)
  ]
}

function isNumericShapeField(layer: ShapeLayer): boolean {
  const selected = layer.vectorFields?.find(field => field.name === layer.selectedField)
  const type = selected?.type?.toLowerCase() || ""
  return ["integer", "int", "real", "double", "float", "number", "numeric", "long"].some(token => type.includes(token))
}

function useShapeLayerValues(layer: ShapeLayer, mutate: (data: any) => void) {
  const colors = layer.colors.fill
  const name = layer.name
  const selectedField = layer.selectedField
  const values = selectedField ? (layer.fieldValues?.[selectedField] || []) : []
  const numericField = selectedField ? isNumericShapeField(layer) : false
  const numericValues = numericField
    ? values.map(value => Number(value)).filter(value => !Number.isNaN(value))
    : []
  const [isLoadingValues, setIsLoadingValues] = React.useState(false)
  const [valuesLoadError, setValuesLoadError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!selectedField || values.length > 0) {
      setIsLoadingValues(false)
      setValuesLoadError(null)
      return
    }

    const geoserverBase = (layer.externalLink || "https://landscapes.wearepal.ai/geoserver").replace(/\/+$/, "")
    const store = layer.identifier.split(":")[0]
    const url = `${geoserverBase}/${store}/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${layer.identifier}&propertyName=${selectedField}&outputFormat=application/json&maxFeatures=1000&srsName=EPSG:3857`
    let cancelled = false
    const abortController = new AbortController()
    const timeoutId = window.setTimeout(() => abortController.abort(), 10000)

    setIsLoadingValues(true)
    setValuesLoadError(null)

    fetch(url, { signal: abortController.signal })
      .then(response => response.ok ? response.json() : Promise.reject(new Error(`Failed to fetch values for ${selectedField}`)))
      .then((geojson) => {
        if (cancelled) return

        const distinctValues = Array.from(new Set(
          (geojson.features || [])
            .map(feature => feature?.properties?.[selectedField])
            .filter(value => value !== undefined && value !== null && value !== "")
            .map(value => value.toString())
        )) as string[]

        distinctValues.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))

        mutate({
          fieldValues: {
            ...(layer.fieldValues || {}),
            [selectedField]: distinctValues
          },
          valueColors: {
            ...(layer.valueColors || {}),
            [selectedField]: (layer.valueColors?.[selectedField] && layer.valueColors[selectedField].length > 0)
              ? layer.valueColors[selectedField]
              : getDefaultShapeLayerValueColors(distinctValues.length, layer.opacity)
          }
        })
      })
      .catch(error => {
        if (cancelled) return
        console.error("Error loading distinct vector field values:", error)
        setValuesLoadError(error?.name === "AbortError" ? "Loading values timed out" : "Could not load values")
      })
      .finally(() => {
        window.clearTimeout(timeoutId)
        if (!cancelled) setIsLoadingValues(false)
      })

    return () => {
      cancelled = true
      abortController.abort()
      window.clearTimeout(timeoutId)
    }
  }, [selectedField, layer.identifier, layer.externalLink, values.length])

  const handleValueColorChange = (hexColor: string, index: number) => {
    if (!selectedField) return

    const [r, g, b] = hexToRgb(hexColor)
    const currentColors = selectedValueColors(layer)
    const nextColors = values.map((_, valueIndex) => {
      const existing = currentColors[valueIndex] || [31, 119, 180, layer.opacity]
      return valueIndex === index ? [r, g, b, existing[3] ?? layer.opacity] as [number, number, number, number] : existing
    })

    mutate({
      valueColors: {
        ...(layer.valueColors || {}),
        [selectedField]: nextColors
      }
    })
  }

  return {
    colors,
    name,
    selectedField,
    values,
    numericField,
    numericValues,
    isLoadingValues,
    valuesLoadError,
    handleValueColorChange
  }
}

export const ShapeLayerSettings = ({ layer, mutate }: ShapeLayerSettingsProps) => {
  const { selectedField, numericField } = useShapeLayerValues(layer, mutate)

  return (
    <>
      {
        layer.vectorFields && layer.vectorFields.length > 0 &&
        <div className="d-flex align-items-center mt-3">
          Style
          <select className="custom-select ml-3" value={selectedField || ""} onChange={e => mutate({ selectedField: e.target.value || undefined })}>
            <option value="">Uniform</option>
            {
              layer.vectorFields.map(field => (
                <option key={field.name} value={field.name}>{field.name}{field.type ? ` (${field.type})` : ""}</option>
              ))
            }
          </select>
        </div>
      }
      {
        selectedField && numericField &&
        <div className="d-flex align-items-center mt-3">
          Fill mode
          <select className="custom-select ml-3" value={layer.fill || "greyscale"} onChange={e => mutate({ fill: e.target.value })}>
            {colMapList}
          </select>
        </div>
      }
    </>
  )
}

export function ShapeLayerLegend({ layer, mutate, toggle }: { layer: ShapeLayer, mutate: (data: any) => void, toggle: boolean }) {
  const { colors, name, selectedField, values, numericField, numericValues, isLoadingValues, valuesLoadError, handleValueColorChange } = useShapeLayerValues(layer, mutate)

  if (toggle) return <></>

  return (
    <div className="p-3 bg-white">
      {
        !selectedField &&
        <>
          <span className="swatch" style={{ backgroundColor: `rgb(${colors.join(",")})` }} />{name}
        </>
      }
      {
        selectedField &&
        <>
          {
            layer.vectorFields && layer.vectorFields.length > 0 &&
            <div className="mb-2 small text-muted">{selectedField}</div>
          }
          {
            numericField && numericValues.length > 0 &&
            <GenericLegend
              min={Math.min(...numericValues)}
              max={Math.max(...numericValues)}
              fill={layer.fill || "greyscale"}
              toggle={false}
            />
          }
          {
            selectedField && isLoadingValues &&
            <span className="text-muted small">Loading values...</span>
          }
          {
            selectedField && valuesLoadError &&
            <span className="text-muted small">{valuesLoadError}</span>
          }
          {
            !numericField && !isLoadingValues && !valuesLoadError && values.length > 0
              ? values.slice(0, 20).map((value, index) => (
                  <div key={value} className="d-flex align-items-center">
                    <div style={{ marginLeft: 4.5, marginRight: "5px" }}>
                      <PaintbrushColorPicker
                        color={(() => {
                          const color = selectedValueColors(layer)[index]
                          if (color) {
                            return rgbToHex(color[0], color[1], color[2])
                          }
                          const fallback = colorForShapeLayerValue(layer, value, index).match(/\d+/g)?.map(Number) || [31, 119, 180]
                          return rgbToHex(fallback[0], fallback[1], fallback[2])
                        })()}
                        onChange={(color) => handleValueColorChange(color, index)}
                        size={20}
                        title={`Change color for ${value}`}
                        placement="left"
                      />
                    </div>
                    <div>{value}</div>
                  </div>
                ))
              : !numericField && !isLoadingValues && !valuesLoadError && <span className="text-muted small">Values load from source data.</span>
          }
          {
            !numericField && values.length > 20 &&
            <div className="small text-muted mt-2">Showing first 20 values</div>
          }
        </>
      }
    </div>
  )
}

interface CropMapLayerSettingsProps {
  layer: CropMapLayer
}

export const CropMapLayerSettings = ({ layer }: CropMapLayerSettingsProps) => (
  <>
    <details className="mt-3">
      <summary>Legend</summary>
      <img src={`https://environment.data.gov.uk/spatialdata/crop-map-of-england-${layer.year}/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetLegendGraphic&FORMAT=image%2Fpng&LAYER=Crop_Map_of_England_${layer.year}_West_Sussex`} />
    </details>
  </>
)

export const CehLandCoverLayerSettings = () => (
  <>
    <details className="mt-3">
      <summary>Legend</summary>
      <span className="swatch" style={{ backgroundColor: "rgb(255, 0, 0)" }} /> Broadleaved woodland<br />
      <span className="swatch" style={{ backgroundColor: "rgb(0, 102, 0)" }} /> Coniferous woodland<br />
      <span className="swatch" style={{ backgroundColor: "rgb(115, 38, 0)" }} /> Arable and horticulture<br />
      <span className="swatch" style={{ backgroundColor: "rgb(0, 255, 0)" }} /> Improved grassland<br />
      <span className="swatch" style={{ backgroundColor: "rgb(127, 229, 127)" }} /> Neutral grassland<br />
      <span className="swatch" style={{ backgroundColor: "rgb(112, 168, 0)" }} /> Calcareous grassland<br />
      <span className="swatch" style={{ backgroundColor: "rgb(153, 129, 0)" }} /> Acid grassland<br />
      <span className="swatch" style={{ backgroundColor: "rgb(255, 255, 0)" }} /> Fen, marsh and swamp<br />
      <span className="swatch" style={{ backgroundColor: "rgb(128, 26, 128)" }} /> Heather<br />
      <span className="swatch" style={{ backgroundColor: "rgb(230, 140, 166)" }} /> Heather grassland<br />
      <span className="swatch" style={{ backgroundColor: "rgb(0, 128, 115)" }} /> Bog<br />
      <span className="swatch" style={{ backgroundColor: "rgb(210, 210, 255)" }} /> Inland rock<br />
      <span className="swatch" style={{ backgroundColor: "rgb(0, 0, 128)" }} /> Saltwater<br />
      <span className="swatch" style={{ backgroundColor: "rgb(0, 0, 255)" }} /> Freshwater<br />
      <span className="swatch" style={{ backgroundColor: "rgb(204, 179, 0)" }} /> Supralittoral rock<br />
      <span className="swatch" style={{ backgroundColor: "rgb(204, 179, 0)" }} /> Supralittoral sediment<br />
      <span className="swatch" style={{ backgroundColor: "rgb(255, 255, 128)" }} /> Littoral rock<br />
      <span className="swatch" style={{ backgroundColor: "rgb(255, 255, 128)" }} /> Littoral sediment<br />
      <span className="swatch" style={{ backgroundColor: "rgb(128, 128, 255)" }} /> Saltmarsh<br />
      <span className="swatch" style={{ backgroundColor: "rgb(0, 0, 0)" }} /> Urban<br />
      <span className="swatch" style={{ backgroundColor: "rgb(128, 128, 128)" }} /> Suburban
    </details>
  </>
)

interface ModelOutputLayerSettingsProps {
  layer: ModelOutputLayer | DatasetLayer
  mutate: (data: any) => void
  layerType: string | undefined
  model?: Data | null
  tileGrid?: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null
}

export function ModelOutputLayerSettings({ layer, mutate, layerType, model = null, tileGrid = null }: ModelOutputLayerSettingsProps) {
  if (layer.fill == "heatmap") {
    layer.fill = "jet"
  }

  const categoricalGrid = tileGrid instanceof CategoricalTileGrid ? tileGrid : null
  const linkedTables = React.useMemo(
    () => (layerType === "CategoricalTileGrid" && categoricalGrid
      ? getLinkedTablesForLayer(model, layer, categoricalGrid)
      : []),
    [layerType, model, layer, categoricalGrid]
  )
  const tableRowViewOptions = React.useMemo(
    () => (linkedTables.length > 0
      ? getCategoricalTableRowViewOptions(model, layer, categoricalGrid)
      : []),
    [linkedTables, model, layer, categoricalGrid]
  )
  const currentView = layer.categoricalView ?? CATEGORICAL_VIEW_CLASSES
  const rowViewActive = isCategoricalTableRowView(layer)
  const parsedTableRowView = parseTableRowView(currentView)
  const [selectedTableNodeId, setSelectedTableNodeId] = React.useState<number>(
    () => parsedTableRowView?.tableNodeId ?? linkedTables[0]?.nodeId ?? -1
  )

  React.useEffect(() => {
    if (linkedTables.length === 0) {
      setSelectedTableNodeId(-1)
      return
    }
    if (!linkedTables.some(table => table.nodeId === selectedTableNodeId)) {
      setSelectedTableNodeId(linkedTables[0].nodeId)
    }
  }, [linkedTables, selectedTableNodeId])

  React.useEffect(() => {
    if (parsedTableRowView && parsedTableRowView.tableNodeId !== selectedTableNodeId) {
      setSelectedTableNodeId(parsedTableRowView.tableNodeId)
    }
  }, [parsedTableRowView, selectedTableNodeId])

  const selectedTable = linkedTables.find(table => table.nodeId === selectedTableNodeId) ?? linkedTables[0]
  const rowOptionsForSelectedTable = React.useMemo(
    () => (selectedTable ? getCategoricalTableRowViewOptionsForTable(selectedTable) : []),
    [selectedTable]
  )

  React.useEffect(() => {
    if (currentView === CATEGORICAL_VIEW_CLASSES) return
    if (!tableRowViewOptions.some(option => option.value === currentView)) {
      mutate({ categoricalView: CATEGORICAL_VIEW_CLASSES })
      return
    }
    if (categoricalGrid && !resolveTableRowMappingForLayer(model, layer, categoricalGrid)) {
      mutate({ categoricalView: CATEGORICAL_VIEW_CLASSES })
    }
  }, [currentView, tableRowViewOptions, mutate, categoricalGrid, model, layer])

  if (layerType === "CategoricalTileGrid" && tableRowViewOptions.length > 0) {
    const multipleTables = linkedTables.length > 1
    const viewOptions = multipleTables ? rowOptionsForSelectedTable : tableRowViewOptions

    return (
      <>
        {multipleTables && (
          <div className="d-flex align-items-center mt-3">
            Table
            <select
              className="custom-select ml-3"
              value={selectedTableNodeId}
              onChange={e => {
                const nextTableNodeId = Number(e.target.value)
                setSelectedTableNodeId(nextTableNodeId)
                const parsed = parseTableRowView(currentView)
                if (parsed && parsed.tableNodeId !== nextTableNodeId) {
                  mutate({ categoricalView: CATEGORICAL_VIEW_CLASSES })
                }
              }}
            >
              {linkedTables.map(table => (
                <option key={table.nodeId} value={table.nodeId}>{table.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="d-flex align-items-center mt-3">
          View
          <select
            className="custom-select ml-3"
            value={currentView}
            onChange={e => mutate({ categoricalView: e.target.value })}
          >
            <option value={CATEGORICAL_VIEW_CLASSES}>Classes</option>
            {viewOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        {rowViewActive && (
          <div className="d-flex align-items-center mt-3">
            Fill mode
            <select className="custom-select ml-3" value={layer.fill} onChange={e => mutate({ fill: e.target.value })}>
              {colMapList}
            </select>
          </div>
        )}
      </>
    )
  }

  if (layerType !== "NumericTileGrid") {
    return (
      <>
      </>
    )
  }

  return (
    <div className="d-flex align-items-center mt-3">
      Fill mode
      <select className="custom-select ml-3" value={layer.fill} onChange={e => mutate({ fill: e.target.value })}>
        {colMapList}
      </select>
    </div>
  )
}

interface IMDLayerSettingsProps {
  layer: IMDLayer
  mutate: (data: any) => void
}

export const IMDLayerSettings = ({ layer, mutate }: IMDLayerSettingsProps) => (
  <>
    <div className="d-flex align-items-center mt-3">
      Property
      <select className="custom-select ml-3" value={layer.property.propName} onChange={e => mutate({ property: IMDProperties.find(prop => prop.propName === e.target.value) })}>
        {
          IMDProperties.map(prop =>
            <option key={prop.propName} value={prop.propName}>
              {prop.name}
            </option>
          )
        }
      </select>
    </div>
    <div className="d-flex align-items-center mt-3">
      Fill mode
      <select className="custom-select ml-3" value={layer.fill} onChange={e => mutate({ fill: e.target.value })}>
        {colMapList}
      </select>
    </div>
  </>
)

// interface MLLayerSettingsProps {
//   layer: MLLayer
// }

// const MLLayerSettings = ({ layer }: MLLayerSettingsProps) => (
//   <details className="mt-3">
//     <summary>Legend</summary>
//     <span className="swatch" style={{ backgroundColor: "#008000" }} /> Hedge<br />
//     <span className="swatch" style={{ backgroundColor: "#ffee00" }} /> Tree<br />
//   </details>
// )

interface KewLayerSettingsProps {
  layer: KewLayer
  mutate: (data: any) => void
}

interface KewPointLayerSettingsProps {
  layer: KewPointLayer
  mutate: (data: any) => void
}

interface KewShapeLayerSettingsProps {  
  layer: KewShapeLayer
  mutate: (data: any) => void
}

export const KewPointLayerSettings = ({ layer, mutate }: KewPointLayerSettingsProps) => {

  return (
    <>
      <div className="d-flex align-items-center mt-3">
        Property
        <select className="custom-select ml-3" value={KewPointOptions[layer.metric].value} onChange={e => mutate({ metric: KewPointOptions.indexOf(KewPointOptions.filter(f => f.value === e.target.value)[0]) })}>
          {
            KewPointOptions.map(opt =>
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            )

          }
        </select>
      </div>
      {/* <div className="d-flex align-items-center mt-3">
        Season
        <select className="custom-select ml-3" value={layer.seasonYear.id} onChange={e => mutate({ seasonYear: seasonYearOptions.filter(f => f.id === +e.target.value)[0] })}>
          {
            seasonYearOptions.map(opt =>
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            )
          }
        </select>
      </div> */}

      <div className="d-flex align-items-center mt-3">
        Fill
          <select className="custom-select ml-3" value={layer.fill} onChange={e => mutate({ fill: e.target.value })}>
            {colMapList}
          </select>
      </div>
    </>
  )
}

export const KewLayerSettings = ({ layer, mutate }: KewLayerSettingsProps) => {


  const handlePeriodChange = (value: string) => {
    const updatedPeriodOptions = layer.periodOptions.map(opt => {
      if (opt.value === value) {
        return { ...opt, selected: !opt.selected }
      }
      return opt
    })
    mutate({ periodOptions: updatedPeriodOptions })
  }

  return (  
    <div className="mt-3">
        <select className="custom-select" value={layer.metric} onChange={e => mutate({ metric: e.target.value })}>
          {
            layer.typeOptions.map(opt =>
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            )
          }
        </select>
        <ul className="list-group mt-1">
          {
            layer.periodOptions.map(opt =>
              <li key={opt.value} className="list-group-item" style={{border: 0, padding: 0}}>
                <input type="checkbox" checked={opt.selected} onChange={() => handlePeriodChange(opt.value)} />
                {opt.label}
              </li>
            )
          }
        </ul>
    </div>
  )
}

export const KewShapeLayerSettings = ({ layer, mutate }: KewShapeLayerSettingsProps) => {
  return <>
      <div className="d-flex align-items-center mt-3">
        Property
      <select className="custom-select ml-3" value={layer.metric.name} onChange={e => mutate({ metric: KewTreeCrownOptions.filter(f => f.name === e.target.value)[0] })}>
          {
            KewTreeCrownOptions.map(opt =>
              <option key={opt.name} value={opt.name}>
                {opt.label}
              </option>
            )

          }
        </select>
      </div>
        {
          layer.metric.name != "none" &&
          <div className="d-flex align-items-center mt-3">
            Fill
            <select className="custom-select ml-3" value={layer.fill} onChange={e => mutate({ fill: e.target.value })}>
              {colMapList}
            </select>
          </div>
        }
  </>
}

type LegendMetaVariant = "editor" | "public"

/** Data-unit labels worth showing. Missing, unknown, and NA stay off the public legend. */
function publicUnitBadgeLabel(units: string | undefined): string | null {
  const value = units?.trim()
  if (!value) return null
  const head = value.split("/")[0]?.trim()
  if (!head || head === "unknown" || head === "No unit" || head === "NA") return null
  return value
}

export function ZoomData({ zoom, area, length, units, variant = "editor" }: {
  zoom: number
  area: number
  length: number
  units?: string
  variant?: LegendMetaVariant
}) {
  const unit = area < 1 ? "cm²" : (area > 1000000 ? "km²" : "m²")
  length = area < 1 ? length * 100 : (area > 1000000 ? length / 1000 : length)
  area = area < 1 ? area * 10000 : (area > 1000000 ? area / 1000000 : area)
  const lengthUnit = unit.slice(0, -1)
  const tooltip = `Rounded to two decimal places. Unrounded values; Area: ${area}${unit}, Resolution: ${length}${lengthUnit}`
  const zoomTier = zoomResolutionTierLabel(zoom)

  if (variant === "public") {
    const unitLabel = publicUnitBadgeLabel(units)
    return (
      <div
        title={`Resolution rounded to two decimal places (${length}${lengthUnit}).`}
        className="legend-zoom-meta pl-3 pb-2 pt-2"
      >
        <div className="legend-zoom-meta__row d-flex align-items-center flex-wrap">
          <span className="badge legend-zoom-meta__badge legend-zoom-meta__badge--public">
            Resolution ~{length.toFixed(2)}{lengthUnit}
          </span>
          {unitLabel ? (
            <span className="badge legend-zoom-meta__badge legend-zoom-meta__badge--public" title="Units">
              {unitLabel}
            </span>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div
      title={tooltip}
      className="legend-zoom-meta pl-3 pb-2 pt-2"
    >
      {units ? (
        <div className="legend-zoom-meta__units text-muted small mb-1">
          Units: {units}
        </div>
      ) : null}
      <div className="legend-zoom-meta__row d-flex align-items-center flex-wrap">
        <span
          className="badge legend-zoom-meta__badge"
          style={{ backgroundColor: zoomResolutionTierBackground(zoom) }}
          title={`${zoomTier} resolution, zoom level ${zoom}`}
        >
          Zoom {zoom}
        </span>
        <span className="legend-zoom-meta__detail">
          Area ~{area.toFixed(2)}{unit}
        </span>
        <span className="legend-zoom-meta__sep" aria-hidden="true">·</span>
        <span className="legend-zoom-meta__detail">
          Resolution ~{length.toFixed(2)}{lengthUnit}
        </span>
      </div>
    </div>
  )
}

export interface ModelOutputLayerLegendProps {
  layer: ModelOutputLayer | DatasetLayer
  getLayerData: (layer: DatasetLayer | ModelOutputLayer) => tileGridStats
  mutateColors: (style: Pick<ModelOutputLayer | DatasetLayer, "colors" | "colorByLabel">) => void
  updateBounds: (overrideBounds: boolean, bounds: [min: number, max: number]) => void
  toggle: boolean
  model?: Data | null
  tileGrid?: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null
  /** Public share legend: resolution and units as badges, without zoom or area. */
  metaVariant?: LegendMetaVariant
}

export function Legend({ colors, minValue, maxValue, type, labels, mutateColors, updateBounds, overrideBounds, bounds, zoom, area, length, toggleCollapse, props, metaVariant = "editor" }) {
  const legendMeta: LegendMetaVariant = metaVariant === "public" ? "public" : "editor"
  const normalizeColor = (
    color: unknown,
    fallback: [number, number, number, number]
  ): [number, number, number, number] => {
    if (Array.isArray(color) && color.length >= 4) {
      return [Number(color[0]), Number(color[1]), Number(color[2]), Number(color[3])]
    }
    return fallback
  }

  if(toggleCollapse)
  {
    return (
      <></>
    )
  }

  if (type === undefined) {
    // if layer is still loading stats will be unavailable.
    return <div></div>
  } else if (type === "BooleanTileGrid") {
    const safeColors: [number, number, number, number][] = [
      normalizeColor(colors?.[0], [0, 0, 0, 0]),
      normalizeColor(colors?.[1], [255, 255, 255, 1]),
    ]

    const data = [{ color: safeColors[0], label: "False" }, { color: safeColors[1], label: "True" }]

    const handleColorChange = (hexColor: string, key: number) => {
      const [r, g, b] = hexToRgb(hexColor)

      const updatedColors = safeColors.map((color: [number, number, number, number], index: number) => {
        return (index === key) ? [r, g, b, 1] : color
      })

      mutateColors(updatedColors)
    }

    return (
      <>
        <div className="color-bar-container-cat">
          <div className="color-bar-legend-cat">
            {data.map(({ color, label }) => (
              <div key={label} className="color-bar-label">
              <div style={{ marginLeft: 4.5, marginRight: "5px" }}>
                <PaintbrushColorPicker
                  color={rgbToHex(color[0], color[1], color[2]) || "#ffffff"}
                  onChange={(hexColor) => handleColorChange(hexColor, label === "False" ? 0 : 1)}
                  size={20}
                  title={`Change ${label} color`}
                  placement="left"
                />
              </div>
              <div className="color-bar-label-text">{label}</div>
              </div>
            ))}
          </div>
        </div>
        <ZoomData
          zoom={zoom}
          area={area}
          length={length}
          units={metaVariant === "public" ? formatTileGridUnitLabel(props, { perArea: true, fallback: "" }) : undefined}
          variant={legendMeta}
        />
      </>
    )

  } else if (type === "CategoricalTileGrid") {

    if (labels) {
      const maxLabelIndex = labels.reduce((max, obj) => Math.max(max, Number(obj.name) || 0), 0)
      const safeColors: [number, number, number, number][] = Array.from({ length: maxLabelIndex }, (_, index) => {
        return normalizeColor(colors?.[index], [0, 0, 0, 0])
      })

      const updateColour = (event: React.ChangeEvent<HTMLInputElement>, key: number) => {
        const checked = event.target.checked
        const updatedColors = safeColors.map((color: [number, number, number, number], index: number) => {
          if (index + 1 === key || key === -1) {
            return [color[0], color[1], color[2], checked ? 1 : 0]
          }
          return color
        })
        mutateColors(updatedColors)
      }

      const handleColorChange = (hexColor: string, key: number) => {
        const [r, g, b] = hexToRgb(hexColor)

        const updatedColors = safeColors.map((color: [number, number, number, number], index: number) => {
          if (index + 1 === key) return [r, g, b, color[3]]
          return color
        })

        mutateColors(updatedColors)
      }

      const data = labels.map(obj => ({
        label: obj.value,
        color: safeColors[obj.name - 1] ?? [0, 0, 0, 0],
        key: obj.name
      }))

      const colors_unchecked = () => {
        for (const innerArray of safeColors) {
          if (innerArray[3] !== 1) {
            return false;
          }
        }
        return true
      }

      return (
        <>
        <div className="color-bar-container-cat legend-cat-compact">
          <div className="color-bar-legend-cat">
            <div className="color-bar-label color-bar-label--all">
              <input type="checkbox" checked={colors_unchecked()} name={"All"} onChange={(event) => updateColour(event, -1)} />
              <div className="color-bar-label-text">All</div>
            </div>
            {data.map(({ color, label, key }) => (
              <div key={label} className="color-bar-label">
                <input type="checkbox" checked={color[3]} name={key} onChange={(event) => updateColour(event, key)} />
                <div className="legend-cat-compact__picker">
                  <PaintbrushColorPicker
                    color={rgbToHex(color[0], color[1], color[2])}
                    onChange={(hexColor) => handleColorChange(hexColor, key)}
                    size={16}
                    title={`Change ${label} color`}
                    placement="left"
                  />
                </div>
                <div className="color-bar-label-text" title={label}>{label}</div>
              </div>
            ))}
          </div>
        </div>
        <ZoomData
          zoom={zoom}
          area={area}
          length={length}
          units={metaVariant === "public" ? formatTileGridUnitLabel(props, { perArea: true, fallback: "" }) : undefined}
          variant={legendMeta}
        />
        </>
      )

    } else {
      return <div></div>
    }


  } else {

    const min = overrideBounds ? bounds[0] : minValue
    const max = overrideBounds ? bounds[1] : maxValue
    const mean = min + (((max) - (min)) * .5)
    const maxIsCapped = metaVariant === "public"
      && !!overrideBounds
      && Number.isFinite(max)
      && Number.isFinite(maxValue)
      && max < maxValue
    const units = formatTileGridUnitLabel(props, { perArea: true, fallback: 'unknown' })

    const toggleBounds = () => {

      if (!bounds) {
        bounds = [minValue, maxValue]
      }

      if (!overrideBounds && bounds[0] === null) {
        bounds = [minValue, maxValue]
      }

      updateBounds(!overrideBounds, bounds)
    }

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = +(e.target.value as string)
      document.getElementById("minOverrideInput")?.classList.remove("text-danger")
      document.getElementById("maxOverrideInput")?.classList.remove("text-danger")
      const boundsValidation = document.getElementById("bounds-validation") as HTMLElement
      boundsValidation.style.display = "none"
      if (!isNaN(val) && val > bounds[1]) {
        document.getElementById("minOverrideInput")?.classList.add("text-danger")
        document.getElementById("maxOverrideInput")?.classList.add("text-danger")
        boundsValidation.style.display = "block"
      }
      updateBounds(overrideBounds, [val, bounds[1]])
    }

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = +(e.target.value as string)
      document.getElementById("minOverrideInput")?.classList.remove("text-danger")
      document.getElementById("maxOverrideInput")?.classList.remove("text-danger")
      const boundsValidation = document.getElementById("bounds-validation") as HTMLElement
      boundsValidation.style.display = "none"
      if (!isNaN(val) && val < bounds[0]) {
        document.getElementById("minOverrideInput")?.classList.add("text-danger")
        document.getElementById("maxOverrideInput")?.classList.add("text-danger")
        boundsValidation.style.display = "block"
      }
      updateBounds(overrideBounds, [bounds[0], val])
    }

    return (
      <>
      <div className="color-bar-container">
        <div className="color-bar">
          {
            colors.map((color) => (
              <div
                key={color.join(",")}
                style={{ backgroundColor: `rgb(${color.join(",")})`, opacity: (isFinite(min) ? 1 : 0.3)}}
                className="color-bar-item"
              />
            ))
          }
        </div>
        { 
          isFinite(min) && 
          <div className="color-bar-legend">
            <div title={min.toString()} >{min.toFixed(3)}</div>
            <div title={mean.toString()} >{mean.toFixed(3)}</div>
            <div title={maxIsCapped ? maxValue.toString() : max.toString()}>{maxIsCapped ? `>${max.toFixed(3)}` : max.toFixed(3)}</div>
          </div>
        }
        {
          !isFinite(min) &&
          <div className="color-bar-legend">
            No numeric data found
          </div>
        }
        {metaVariant !== "public" && (
          <>
          <div style={{ padding: 15, paddingTop: 0, paddingLeft: 35 }} className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="overrideToggle"
              checked={overrideBounds}
              onChange={toggleBounds}
            />
            <label className="form-check-label" htmlFor="overrideToggle">
              Set custom bounds
            </label>
          </div>
          {overrideBounds && (
            <>
            <div style={{ paddingRight: 15, paddingLeft: 15 }} className="row override-inputs">
              <div className="col">
                <label htmlFor="minOverrideInput" className="form-label">
                  Min
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="minOverrideInput"
                  value={bounds[0]}
                  onChange={handleMinChange}
                />
              </div>
              <div className="col">
                <label htmlFor="maxOverrideInput" className="form-label">
                  Max
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="maxOverrideInput"
                  value={bounds[1]}
                  onChange={handleMaxChange}
                />
              </div>
            </div>
            <div style={{ display: "none" }} id="bounds-validation" className="pl-3 text-center text-danger">
              invalid bounds
            </div>
            </>
          )}
          </>
        )}
      </div>
      <ZoomData
        zoom={zoom}
        area={area}
        length={length}
        units={metaVariant === "public" ? formatTileGridUnitLabel(props, { perArea: true, fallback: "" }) : units}
        variant={legendMeta}
      />
      </>
    )
  }

}

export function ModelOutputLayerLegend({ layer, getLayerData, mutateColors, updateBounds, toggle, model = null, tileGrid = null, metaVariant = "editor" }: ModelOutputLayerLegendProps) {

  const rawStats = getLayerData(layer)
  const stats = effectiveLegendStatsForLayer(layer, tileGrid, rawStats, model)
  const labelEntries = rawStats.type === "CategoricalTileGrid"
    ? labelEntriesFromStatsLabels(rawStats.labels)
    : []
  const maxKey = labelEntries.reduce((max, entry) => Math.max(max, entry.key), stats.max ?? 0)

  const colors = stats.type === "NumericTileGrid"
    ? getColorStops((layer.fill == "greyscale" ? "greys" : (layer.fill === "heatmap" ? "jet" : layer.fill)), 50).filter(c => typeof c !== "number").reverse()
    : stats.type === "CategoricalTileGrid"
      ? resolveCategoricalPalette(
          labelEntries,
          maxKey,
          { colors: layer.colors, colorByLabel: layer.colorByLabel },
          index => layer.colors?.[index] ?? [0, 0, 0, 0]
        )
      : layer.colors

  const handleMutateColors = (updatedColors: [number, number, number, number][]) => {
    if (rawStats.type !== "CategoricalTileGrid" || isCategoricalTableRowView(layer)) {
      mutateColors({ colors: updatedColors })
      return
    }
    mutateColors(
      applyCategoricalColorMutation(
        labelEntries,
        maxKey,
        updatedColors,
        { colors: layer.colors, colorByLabel: layer.colorByLabel },
        true
      )
    )
  }

  return (
    <div>
      <Legend 
        colors={colors} 
        minValue={stats.min} 
        maxValue={stats.max} 
        type={stats.type} 
        labels={stats.labels}
        mutateColors={handleMutateColors} 
        updateBounds={updateBounds} 
        overrideBounds={layer.overrideBounds} 
        bounds={layer.bounds} 
        zoom={stats.zoom} 
        area={stats.area} 
        length={stats.length} 
        toggleCollapse={toggle}
        props={stats.props}
        metaVariant={metaVariant}
      />
    </div>
  )
}

interface SidebarProps {
  state: State
  selectLayer: (id: number | undefined) => void
  mutateLayer: (id: number, data: Partial<Layer>) => void
  deleteLayer: (id: number) => void
  mapLayerStack: MapStackSlot[]
  reorderMapLayers: (slots: MapStackSlot[]) => void
  localGeotiffLayers: Record<string, LocalGeotiffLayerState>
  mutateLocalGeotiff: (id: string, data: Partial<Pick<LocalGeotiffLayerState, 'name' | 'opacity' | 'visible'>>) => void
  removeLocalGeotiff: (id: string) => void
  selectedLocalGeotiffId: string | null
  onSelectLocalGeotiff: (localId: string) => void
  showLayerPalette: () => void
  hide: () => void
  getLayerData: (layer: DatasetLayer | ModelOutputLayer) => tileGridStats
  getTileGrid?: (layer: DatasetLayer | ModelOutputLayer) => BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null
  selectedLayer: Layer | null
  setSelectedLayer: (layer: Layer | null) => void
  exportLayerAsGeoTiff: (layer: ModelOutputLayer | DatasetLayer) => Promise<void>
  exportLayerAsShapefile: (layer: ModelOutputLayer | DatasetLayer) => Promise<void>
  saveLayerAsDataset: (layer: ModelOutputLayer | DatasetLayer) => Promise<void>
  /** When the user edits the layer name in the sidebar for a Model output, sync to the node so it is not overwritten on next process */
  updateModelOutputNodeName?: (nodeId: number, name: string) => void
  model: Data | null
  modelEditorApi: ModelEditorApi | null
  highlightedCategoryGroupKey?: string | null
  downloading: string | null
  /** When false the sidebar stays mounted (to keep Layers/Detail) but AEGIS chat unmounts. */
  visible?: boolean
  exportStatus: {
    status: 'idle' | 'preparing' | 'uploading' | 'processing' | 'downloading' | 'completed' | 'error'
    progress: number
    message: string
    error?: string
  }
}

export interface GenericLegendProps {
  min: string | number
  max: number | string
  fill: string
  toggle: boolean
  hideLabels?: boolean
}

export function PaintbrushGeoJSONLegend({ layer, toggle, mutate }: { layer: PaintbrushGeoJSONLayer; toggle: boolean; mutate?: (data: any) => void }) {
  if (toggle) {
    return <></>
  }
  const rows = layer.legendClasses
  const hidden = new Set((layer.hiddenClassNames || []).map(name => name.trim().toLowerCase()).filter(Boolean))
  if (!rows || rows.length === 0) {
    return (
      <div className="p-3 bg-white">
        <span className="small text-muted">Class legend appears when class metadata is available for this Paintbrush.</span>
      </div>
    )
  }
  return (
    <div className="p-3 bg-white">
      {rows.map((row, index) => (
        <div key={`pb-${index}-${row.className}`} className="d-flex align-items-center mb-2">
          <input
            type="checkbox"
            className="mr-2"
            checked={!hidden.has(row.className.trim().toLowerCase())}
            onChange={() => {
              if (!mutate) return
              const key = row.className.trim().toLowerCase()
              const next = new Set(hidden)
              if (next.has(key)) next.delete(key)
              else next.add(key)
              mutate({ hiddenClassNames: Array.from(next.values()) })
            }}
            title={`Show/hide ${row.className}`}
          />
          <span
            className="rounded mr-2 flex-shrink-0"
            style={{
              width: 22,
              height: 16,
              backgroundColor: row.color,
              border: "1px solid rgba(0,0,0,0.12)",
            }}
            title={row.color}
          />
          <span className="small text-truncate" title={row.className}>
            {row.className}
          </span>
        </div>
      ))}
    </div>
  )
}

export function GenericLegend({ min, max, fill, toggle, hideLabels = false }: GenericLegendProps) {
  // Generic legend component for continuous data

  if(toggle){
    return (
      <></>
    )
  }

  const cols = getColorStops(fill == "greyscale" ? "greys" : fill, 60).filter(c => typeof c !== "number").reverse()

  return (
    <>
      <div className="color-bar-container">
        <div className="color-bar">
          {
            cols.map((color) => (
              <div
                key={color.join(",")}
                style={
                  { 
                    backgroundColor: `rgb(${color.join(",")})`, 
                    opacity: 1
                  }
                }
                className="color-bar-item"
              />
            ))
          }
        </div>
      </div>
      {
        !hideLabels &&
        <div className="color-bar-legend">
          <div title={min.toString()} >{min}</div>
          <div title={max.toString()} >{max}</div>
        </div>
      }
    </>
  )
}

export function GeoserverLayerLegend({ layer, mutate, toggle }: { layer: GeoserverLayer, mutate: (data: any) => void, toggle: boolean }) {
  if (toggle) return <></>

  if (layer.categoricalRaster) {
    const entries = geoserverCategoricalEntries(layer)
    const defaultColors = getDefaultRasterCategoryColors(entries.length)
    const colors = entries.map((_, index) => selectedRasterValueColors(layer)[index] || defaultColors[index])

    return (
      <div className="p-3 bg-white">
        {
          entries.length > 0
            ? entries.slice(0, 20).map((entry, index) => {
                const color = colors[index] || [31, 119, 180, 1]
                return (
                  <div key={entry.value} className="d-flex align-items-center">
                    <div style={{ marginLeft: 4.5, marginRight: "5px" }}>
                      <PaintbrushColorPicker
                        color={rgbToHex(color[0], color[1], color[2])}
                        onChange={(hex) => {
                          const [r, g, b] = hexToRgb(hex)
                          const nextColors = entries.map((_, valueIndex) => {
                            const existing = colors[valueIndex] || [31, 119, 180, 1]
                            if (valueIndex !== index) return existing
                            return [r, g, b, existing[3] ?? 1] as [number, number, number, number]
                          })
                          mutate({ valueColors: nextColors })
                        }}
                        size={20}
                        title={`Change color for ${entry.label}`}
                        placement="left"
                      />
                    </div>
                    <div>{entry.label}</div>
                  </div>
                )
              })
            : <span className="text-muted small">Categorical raster values load after publish.</span>
        }
        {
          entries.length > 20 &&
          <div className="small text-muted mt-2">Showing first 20 values</div>
        }
      </div>
    )
  }

  return (
    <GenericLegend
      min={layer.rasterMinValue ?? 0}
      max={layer.rasterMaxValue ?? 255}
      fill={layer.fill || "greyscale"}
      toggle={false}
      hideLabels={false}
    />
  )
}

enum LayersPanelTab {
  Layers,
  Detail,
  Aegis,
}

const layersPanelTabButtonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 0,
  fontSize: "0.82rem",
  padding: "10px 4px 8px",
  backgroundColor: "transparent",
  color: "#8492a6",
  fontWeight: 500,
  letterSpacing: "0.02em",
  borderBottom: "2px solid transparent",
  transition: "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease",
}

const layersPanelActiveTabButtonStyle: React.CSSProperties = {
  color: "#3b4a87",
  backgroundColor: "#ffffff",
  borderBottom: "2px solid #6471c0",
  fontWeight: 600,
}

export function layerSettingsTypeLabel(
  layer: Layer | null,
  gridType?: tileGridStats["type"]
): string | undefined {
  if (!layer) return undefined
  if ((layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer") && gridType) {
    if (gridType === "NumericTileGrid") return "Numeric"
    if (gridType === "BooleanTileGrid") return "Boolean"
    if (gridType === "CategoricalTileGrid") return "Categorical"
  }
  return shortLabelForLayerType(layer.type)
}

export function LayerSettingsHeader({
  title = "Layer settings",
  collapsed,
  onToggle,
  typeLabel,
  fillLabel,
  valueLabel,
  valueLabels,
  compact = false,
  keepTitle = false,
  hasComment = false,
  commentOpen = false,
  onToggleComment,
}: {
  title?: string
  collapsed: boolean
  onToggle: () => void
  typeLabel?: string
  fillLabel?: string
  valueLabel?: string
  valueLabels?: string[]
  compact?: boolean
  keepTitle?: boolean
  hasComment?: boolean
  commentOpen?: boolean
  onToggleComment?: () => void
}) {
  const collapsedValueBadges = valueLabels ?? (valueLabel !== undefined ? [valueLabel] : [])
  return (
    <div
      className={`d-flex align-items-center layer-settings-header border-top border-bottom bg-light${compact ? " layer-settings-header--compact px-2 py-1" : " px-3 py-2"}`}
    >
      <button
        type="button"
        className="layer-settings-toggle"
        aria-expanded={!collapsed}
        aria-label={title}
        onClick={onToggle}
      >
        {(!collapsed || keepTitle) && <span className="layer-settings-title">{title}</span>}
        {typeLabel && <span className="badge layer-settings-type-badge">{typeLabel}</span>}
        {fillLabel && <span className="badge layer-settings-type-badge">{fillLabel}</span>}
        {collapsed && collapsedValueBadges.map(label => (
          <span key={label} className="badge layer-settings-opacity-badge">{label}</span>
        ))}
      </button>
      {onToggleComment && (
        <button
          type="button"
          className={`layer-settings-comment-btn${hasComment || commentOpen ? " is-active" : ""}`}
          aria-expanded={commentOpen}
          aria-label={hasComment ? "Edit layer comment" : "Add layer comment"}
          title={hasComment ? "Edit comment" : "Add comment"}
          onClick={event => {
            event.stopPropagation()
            onToggleComment()
          }}
        >
          <i className="fas fa-comment-alt" />
        </button>
      )}
      <button
        type="button"
        className="layer-settings-chevron-btn"
        tabIndex={-1}
        aria-hidden="true"
        onClick={onToggle}
      >
        <i className={`fas fa-fw fa-chevron-${collapsed ? "up" : "down"} text-dark`} />
      </button>
    </div>
  )
}

function prettyFillName(fill?: string): string | undefined {
  if (!fill) return undefined
  if (fill === "greyscale") return "Greyscale"
  if (fill === "heatmap") return "Heatmap"
  if (fill.length === 0) return undefined
  return fill.charAt(0).toUpperCase() + fill.slice(1)
}

export function layerSettingsFillLabel(
  layer: Layer | null,
  gridType?: tileGridStats["type"]
): string | undefined {
  if (!layer) return undefined
  if (layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer") {
    if (gridType !== "NumericTileGrid") return undefined
    return prettyFillName(layer.fill)
  }
  if (layer.type === "ShapeLayer") return prettyFillName(layer.fill)
  if (layer.type === "GeoserverLayer") {
    if (layer.categoricalRaster) return undefined
    return prettyFillName(layer.fill)
  }
  if (
    layer.type === "IMDLayer" ||
    layer.type === "KewPointLayer" ||
    layer.type === "KewShapeLayer" ||
    layer.type === "WFSLayer"
  ) {
    return prettyFillName(layer.fill)
  }
  return undefined
}

export function layerLegendStyleLabel(
  layer: Layer | null,
  gridType?: tileGridStats["type"]
): string | undefined {
  if (!layer) return undefined
  if (layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer") {
    if (gridType === "CategoricalTileGrid" && isCategoricalTableRowView(layer)) return "Table"
    if (gridType === "BooleanTileGrid") return "Boolean"
  }
  return undefined
}

function formatLegendBound(value: number): string {
  if (!Number.isFinite(value)) return "—"
  if (Number.isInteger(value)) return String(value)
  return parseFloat(value.toPrecision(4)).toString()
}

function numericLegendRange(
  layer: Layer,
  stats?: Pick<tileGridStats, "min" | "max" | "type">
): { min: number; max: number } | undefined {
  if (layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer") {
    if (stats?.type !== "NumericTileGrid") return undefined
    if (
      layer.overrideBounds &&
      layer.bounds &&
      Number.isFinite(layer.bounds[0]) &&
      Number.isFinite(layer.bounds[1])
    ) {
      return { min: layer.bounds[0], max: layer.bounds[1] }
    }
    if (Number.isFinite(stats.min) && Number.isFinite(stats.max)) {
      return { min: stats.min, max: stats.max }
    }
    return undefined
  }
  if (layer.type === "GeoserverLayer" && layer.fill && !layer.categoricalRaster) {
    return { min: layer.rasterMinValue ?? 0, max: layer.rasterMaxValue ?? 255 }
  }
  if (layer.type === "KewPointLayer" && (layer.min != null || layer.max != null)) {
    return { min: layer.min ?? 0, max: layer.max ?? 0 }
  }
  if (layer.type === "KewShapeLayer" && layer.metric.name !== "none" && (layer.min != null || layer.max != null)) {
    return { min: layer.min ?? 0, max: layer.max ?? 0 }
  }
  if (layer.type === "WFSLayer") {
    const output = natmap_outputs[layer.propIdx]
    if (output) return { min: output.min, max: output.max }
  }
  return undefined
}

export function layerLegendValueLabels(
  layer: Layer | null,
  gridType?: tileGridStats["type"],
  classCount?: number,
  stats?: Pick<tileGridStats, "min" | "max" | "type">
): string[] | undefined {
  if (!layer) return undefined
  const range = numericLegendRange(layer, stats)
  if (range) {
    return [`min:${formatLegendBound(range.min)}`, `max:${formatLegendBound(range.max)}`]
  }
  if (layer.type === "PaintbrushGeoJSONLayer") {
    const count = layer.legendClasses?.length
    return count != null ? [`classes:${count}`] : undefined
  }
  if (layer.type === "GeoserverLayer" && layer.categoricalRaster && layer.categoricalValueCount != null) {
    return [`classes:${layer.categoricalValueCount}`]
  }
  if (
    (layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer") &&
    gridType === "CategoricalTileGrid" &&
    classCount != null
  ) {
    return [`classes:${classCount}`]
  }
  return undefined
}

export function layerLegendValueLabel(
  layer: Layer | null,
  gridType?: tileGridStats["type"],
  classCount?: number,
  stats?: Pick<tileGridStats, "min" | "max" | "type">
): string | undefined {
  return layerLegendValueLabels(layer, gridType, classCount, stats)?.[0]
}

export function collapsedLegendValueLabels(
  layer: Layer | null,
  getLayerData?: (layer: DatasetLayer | ModelOutputLayer) => tileGridStats,
  tileGrid?: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null,
  model?: Data | null
): string[] | undefined {
  if (!layer) return undefined
  if ((layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer") && getLayerData) {
    const rawStats = getLayerData(layer)
    const stats = effectiveLegendStatsForLayer(layer, tileGrid, rawStats, model ?? null)
    return layerLegendValueLabels(layer, stats.type, rawStats.labels?.length, stats)
  }
  return layerLegendValueLabels(layer)
}

export const Sidebar = ({ state, selectLayer, mutateLayer, deleteLayer, mapLayerStack, reorderMapLayers, localGeotiffLayers, mutateLocalGeotiff, removeLocalGeotiff, selectedLocalGeotiffId, onSelectLocalGeotiff, showLayerPalette, hide, getLayerData, getTileGrid, selectedLayer, setSelectedLayer, exportLayerAsGeoTiff, exportLayerAsShapefile, saveLayerAsDataset, updateModelOutputNodeName, model, modelEditorApi, highlightedCategoryGroupKey = null, downloading, exportStatus, visible = true }: SidebarProps) => {
  React.useEffect(() => {
    setSelectedLayer(state.selectedLayer === undefined ? null : state.project.layers[state.selectedLayer])
  }, [setSelectedLayer, state.selectedLayer, state.project.layers])
  const selectedLocalLayer = selectedLocalGeotiffId !== null ? localGeotiffLayers[selectedLocalGeotiffId] : null
  const [isLegCollapsed, setIsLegCollapsed] = React.useState<boolean>(false)
  const [settingsCollapsed, setSettingsCollapsed] = React.useState(false)
  const [commentOpen, setCommentOpen] = React.useState(false)
  React.useEffect(() => {
    setCommentOpen(false)
  }, [state.selectedLayer])
  const [lastNonAegisTab, setLastNonAegisTab] = React.useState<LayersPanelTab.Layers | LayersPanelTab.Detail>(LayersPanelTab.Layers)
  const { aegisTabOpen, setAegisTabOpen, isSending } = useAegisSession()
  const highlightedOutputNodeId = useMapOutputHighlightSnapshot().highlightedNodeId
  React.useEffect(() => {
    if (highlightedOutputNodeId == null) return
    document
      .querySelector(`[data-map-output-node="${highlightedOutputNodeId}"]`)
      ?.scrollIntoView({ block: "nearest" })
  }, [highlightedOutputNodeId])
  const [expandedChartId, setExpandedChartId] = useExpandedChartId()
  const expandedChart = React.useMemo(
    () => getChartDetailEntry(model, expandedChartId),
    [model, expandedChartId]
  )
  const activePanelTab = aegisTabOpen ? LayersPanelTab.Aegis : lastNonAegisTab
  const toggleLegend = () => setIsLegCollapsed(prevState => !prevState)

  const selectLayersTab = React.useCallback(() => {
    setLastNonAegisTab(LayersPanelTab.Layers)
    setAegisTabOpen(false)
  }, [setAegisTabOpen])

  const selectDetailTab = React.useCallback(() => {
    setLastNonAegisTab(LayersPanelTab.Detail)
    setAegisTabOpen(false)
  }, [setAegisTabOpen])

  const availableCategoryGroupKeys = React.useMemo(() => {
    const keys = new Set<string>()
    for (const entry of getCategoricalToNumericDetailEntries(model)) {
      for (const row of entry.rows) {
        keys.add(categoryGroupKeyForLabel(row.label))
      }
    }
    availableTableColumnGroupKeys(model).forEach(key => {
      keys.add(key)
    })
    return keys
  }, [model])

  React.useEffect(() => {
    if (!highlightedCategoryGroupKey) return
    if (!availableCategoryGroupKeys.has(highlightedCategoryGroupKey)) return
    setLastNonAegisTab(LayersPanelTab.Detail)
    setAegisTabOpen(false)
  }, [highlightedCategoryGroupKey, availableCategoryGroupKeys, setAegisTabOpen])

  const modelDatasetStyleEntries = React.useMemo(
    () => buildModelDatasetStyleCopyEntries(state.project.layers, getLayerData),
    [state.project.layers, getLayerData]
  )

  return <div className="d-flex flex-column project-sidebar" style={{ width: "400px", minHeight: 0, height: "100%" }}>
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
    <div style={{ borderBottom: "1px solid #dfe4f4", backgroundColor: "#f8f9fc" }}>
      <div className="d-flex align-items-stretch border-top">
        <div className="d-flex flex-grow-1">
          <button
            type="button"
            className="flex-grow-1 project-layers-panel-tab"
            onClick={selectLayersTab}
            style={{
              ...layersPanelTabButtonStyle,
              ...(activePanelTab === LayersPanelTab.Layers ? layersPanelActiveTabButtonStyle : {}),
            }}
          >
            <i className="fas fa-layer-group mr-1" style={{ fontSize: "0.8rem" }} />
            Layers
          </button>
          <button
            type="button"
            className="flex-grow-1 project-layers-panel-tab"
            onClick={selectDetailTab}
            style={{
              ...layersPanelTabButtonStyle,
              ...(activePanelTab === LayersPanelTab.Detail ? layersPanelActiveTabButtonStyle : {}),
            }}
          >
            <i className="fas fa-sliders-h mr-1" style={{ fontSize: "0.8rem" }} />
            Detail
          </button>
          <button
            type="button"
            className="flex-grow-1 project-layers-panel-tab"
            onClick={() => setAegisTabOpen(true)}
            aria-busy={isSending}
            title={isSending ? "AEGIS is running" : undefined}
            style={{
              ...layersPanelTabButtonStyle,
              ...(activePanelTab === LayersPanelTab.Aegis ? layersPanelActiveTabButtonStyle : {}),
            }}
          >
            <i className={`fas ${isSending ? "fa-spinner fa-spin" : "fa-comments"} mr-1`} style={{ fontSize: "0.8rem" }} />
            AEGIS
          </button>
        </div>
        <button
          type="button"
          className="border-0 bg-transparent px-2"
          style={{ color: "#8492a6", borderLeft: "1px solid #dfe4f4" }}
          onClick={hide}
          title="Collapse panel"
        >
          <i className="fas fa-angle-double-right" />
        </button>
      </div>
      <style>{`
        .project-layers-panel-tab:hover { color: #3b4a87 !important; background-color: #eef2ff !important; }
        .project-layers-panel-tab:focus { outline: none; box-shadow: none; }
      `}</style>
    </div>
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      {activePanelTab === LayersPanelTab.Layers && (
        <>
          <div className="d-flex justify-content-end px-3 py-2 bg-light border-bottom">
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={showLayerPalette}>
              <i className="fas fa-plus mr-1" />
              Add layer
            </button>
          </div>
          <div
            className="flex-grow-1 bg-white"
            style={{ overflowY: "auto", flexBasis: "0px" }}
            onClick={() => selectLayer(undefined)}
          >
      <ReactSortable
        list={mapLayerStack.slice().reverse().map(slot => ({ id: slotToKey(slot) }))}
        setList={(list: { id: string }[]) => {
          const nextSlots = list.map(item => keyToSlot(item.id)).reverse()
          reorderMapLayers(nextSlots)
        }}
      >
        {
          mapLayerStack.slice().reverse().map(slot => {
            if (slot.kind === "project") {
              const id = slot.layerId
              const layer = state.project.layers[id]
              if (layer === undefined) {
                return null
              }
              const isDeleted = layer.type == "DatasetLayer" && layer.deleted === true
              const paletteColor = (layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer") && layer.colors?.[0] && layer.colors[0][3] !== 0
                ? `rgba(${layer.colors[0].join(",")})`
                : null
              const iconColor = layer.type === "ShapeLayer"
                ? `rgb(${layer.colors.fill.join(',')})`
                : paletteColor ?? "rgb(96,96,96)"

              return (
                <div
                  key={slotToKey(slot)}
                  data-map-output-node={layer.type === "ModelOutputLayer" ? layer.nodeId : undefined}
                >
                  <LayerListRow
                    name={layer.name}
                    iconClassName={iconForLayerType(layer.type)}
                    iconColor={iconColor}
                    appearance="default"
                    visible={Boolean(layer.visible)}
                    selected={id === state.selectedLayer && selectedLocalGeotiffId === null}
                    highlighted={layer.type === "ModelOutputLayer" && layer.nodeId === highlightedOutputNodeId}
                    dimmed={isDeleted}
                    title={isDeleted ? "Dataset is unavailable." : ""}
                    onSelect={(e) => {
                      e.stopPropagation()
                      selectLayer(id === state.selectedLayer ? undefined : id)
                    }}
                    onToggleVisibility={(e) => {
                      e.stopPropagation()
                      mutateLayer(id, { visible: !layer.visible })
                    }}
                  />
                </div>
              )
            }

            const local = localGeotiffLayers[slot.localId]
            const name = local?.name ?? "Local GeoTIFF"
            return (
              <div key={slotToKey(slot)}>
                <LayerListRow
                  name={name}
                  iconClassName="fa-image"
                  iconColor="rgb(96,96,96)"
                  appearance="default"
                  visible={local?.visible !== false}
                  selected={slot.localId === selectedLocalGeotiffId}
                  title="Local GeoTIFF — not saved with the project"
                  onSelect={(e) => {
                    e.stopPropagation()
                    onSelectLocalGeotiff(slot.localId)
                  }}
                  onToggleVisibility={(e) => {
                    e.stopPropagation()
                    if (!local) {
                      return
                    }
                    mutateLocalGeotiff(slot.localId, { visible: !local.visible })
                  }}
                  deleteTitle="Remove local preview"
                  onDelete={(e) => {
                    e.stopPropagation()
                    removeLocalGeotiff(slot.localId)
                  }}
                />
              </div>
            )
          })
        }
      </ReactSortable>
          </div>
        </>
      )}
      {activePanelTab === LayersPanelTab.Detail && (
        <ModelDetailPanel
          model={model}
          modelEditorApi={modelEditorApi}
          highlightedCategoryGroupKey={highlightedCategoryGroupKey}
        />
      )}
      {activePanelTab === LayersPanelTab.Aegis && visible && <AegisMapTab />}
      <ChartDetailExpandedDialog
        entry={expandedChart}
        onClose={() => setExpandedChartId(null)}
      />
    </div>
    </div>
    {activePanelTab !== LayersPanelTab.Aegis && (
    <div>
    {
      (selectedLayer?.type == "ModelOutputLayer" || selectedLayer?.type == "DatasetLayer" || selectedLayer?.type == "IMDLayer" || selectedLayer?.type == "KewPointLayer" || selectedLayer?.type == "WFSLayer" || selectedLayer?.type == "ShapeLayer" || (selectedLayer?.type == "GeoserverLayer" && !!selectedLayer.fill) || (selectedLayer?.type == "KewShapeLayer" && selectedLayer.metric.name !== "none") || selectedLayer?.type == "PaintbrushGeoJSONLayer") && (
        <LayerSettingsHeader
          title="Layer legend"
          keepTitle
          collapsed={isLegCollapsed}
          onToggle={toggleLegend}
          typeLabel={layerLegendStyleLabel(
            selectedLayer,
            selectedLayer && isModelDatasetLayer(selectedLayer)
              ? getLayerData(selectedLayer).type
              : undefined
          )}
          valueLabels={collapsedLegendValueLabels(
            selectedLayer,
            selectedLayer && isModelDatasetLayer(selectedLayer) ? getLayerData : undefined,
            selectedLayer && isModelDatasetLayer(selectedLayer) ? getTileGrid?.(selectedLayer) ?? null : undefined,
            model
          )}
        />
      )
    }
    {
      (selectedLayer?.type == "ModelOutputLayer" || selectedLayer?.type == "DatasetLayer") &&
      <ModelOutputLayerLegend
        layer={selectedLayer}
        getLayerData={getLayerData}
        mutateColors={styleUpdate => state.selectedLayer !== undefined && mutateLayer(state.selectedLayer, styleUpdate)}
        updateBounds={(overrideBounds, bounds) => state.selectedLayer !== undefined && mutateLayer(state.selectedLayer, { overrideBounds, bounds })}
        toggle={isLegCollapsed}
        model={model}
        tileGrid={getTileGrid?.(selectedLayer) ?? null}
      />
    }
    {
      selectedLayer?.type == "ShapeLayer" &&
      <ShapeLayerLegend
        layer={selectedLayer}
        mutate={
          data => state.selectedLayer !== undefined &&
            mutateLayer(state.selectedLayer, data)
        }
        toggle={isLegCollapsed}
      />
    }
    {
      selectedLayer?.type == "GeoserverLayer" &&
      selectedLayer.fill &&
      <GeoserverLayerLegend
        layer={selectedLayer}
        mutate={
          data => state.selectedLayer !== undefined &&
            mutateLayer(state.selectedLayer, data)
        }
        toggle={isLegCollapsed}
      />
    }
    {
      selectedLayer?.type == "IMDLayer" &&
      <GenericLegend
        min={`Least (${selectedLayer.property.min}${selectedLayer.property.min == 10 ? "th Decile" : ""})`}
        max={`Most (${selectedLayer.property.max}${selectedLayer.property.min == 10 ? "st Decile" : ""})`}
        fill={selectedLayer.fill}
        toggle={isLegCollapsed}
      />
    }
    {
      selectedLayer?.type == "KewPointLayer" && 
      <GenericLegend
        min={`${selectedLayer.min ?? "Least"}`}
        max={`${selectedLayer.max ?? "Most"}`}
        fill={selectedLayer.fill}
        toggle={isLegCollapsed}
      />
    }
    {
      selectedLayer?.type == "WFSLayer" &&
      <GenericLegend
        min={natmap_outputs[selectedLayer.propIdx].min}
        max={natmap_outputs[selectedLayer.propIdx].max}
        fill={selectedLayer.fill}
        toggle={isLegCollapsed}
      />
    }
    {
      selectedLayer?.type == "KewShapeLayer" &&
      selectedLayer.metric.name !== "none" &&
      <GenericLegend
        min={`${selectedLayer.min?.toFixed(4) ?? "Least"}`}
        max={`${selectedLayer.max?.toFixed(4) ?? "Most"}`}
        fill={selectedLayer.fill}
        toggle={isLegCollapsed}
      />
    }
    {
      selectedLayer?.type == "PaintbrushGeoJSONLayer" &&
      <PaintbrushGeoJSONLegend
        layer={selectedLayer}
        toggle={isLegCollapsed}
        mutate={data => state.selectedLayer !== undefined && mutateLayer(state.selectedLayer, data)}
      />
    }
    <LayerSettingsHeader
      collapsed={settingsCollapsed}
      onToggle={() => setSettingsCollapsed(collapsed => !collapsed)}
      valueLabel={
        selectedLocalLayer
          ? `opacity:${Math.round(selectedLocalLayer.opacity * 100)}%`
          : selectedLayer
            ? `opacity:${Math.round(selectedLayer.opacity * 100)}%`
            : undefined
      }
      hasComment={Boolean(selectedLayer?.comment?.trim())}
      commentOpen={commentOpen}
      onToggleComment={selectedLayer ? () => setCommentOpen(open => !open) : undefined}
    />
    {commentOpen && selectedLayer && state.selectedLayer !== undefined && (
      <div className="px-3 py-2 bg-white border-bottom">
        <textarea
          className="form-control form-control-sm"
          rows={3}
          placeholder="Shown on the public map when the info icon is hovered. Use markdown for headings (# Title) and pictures (![caption](https://…))."
          aria-label="Layer comment"
          value={selectedLayer.comment ?? ""}
          onChange={event => {
            if (state.selectedLayer === undefined) return
            mutateLayer(state.selectedLayer, { comment: event.target.value })
          }}
        />
      </div>
    )}
    <>
    {!settingsCollapsed && (
    <div id="layer-settings-body" className="p-3 bg-white text-nowrap" style={{ maxHeight: "50vh", overflowY: "auto" }}>
      {
        selectedLocalLayer !== null && selectedLocalGeotiffId !== null ?
          <>
            <p className="small text-muted mb-2">
              Local GeoTIFF preview — not included when you save the project.
            </p>
            <input
              type="text"
              className="form-control"
              placeholder="Layer name"
              value={selectedLocalLayer.name}
              title={selectedLocalLayer.name}
              onChange={
                e => mutateLocalGeotiff(selectedLocalGeotiffId, { name: e.target.value })
              }
            />
            <div className="d-flex align-items-center mt-3">
              Opacity
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                className="custom-range ml-3"
                value={selectedLocalLayer.opacity}
                onChange={
                  e => mutateLocalGeotiff(selectedLocalGeotiffId, { opacity: Number(e.target.value) })
                }
              />
            </div>
          </>
        : selectedLayer !== null ?
          <>
            <input
              type="text"
              className="form-control"
              placeholder="Layer name"
              value={selectedLayer?.name}
              title={selectedLayer?.name}
              onChange={
                e => {
                  if (state.selectedLayer === undefined) return
                  const name = e.target.value
                  mutateLayer(state.selectedLayer, { name })
                  if (selectedLayer?.type === "ModelOutputLayer" && updateModelOutputNodeName) {
                    updateModelOutputNodeName(selectedLayer.nodeId, name)
                  }
                }
              }
            />
            <div className="d-flex align-items-center mt-3">
              Opacity
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                className="custom-range ml-3"
                value={selectedLayer.opacity}
                onChange={
                  e => state.selectedLayer !== undefined &&
                    mutateLayer(state.selectedLayer, { opacity: Number(e.target.value) })
                }
              />
            </div>
            {(() => {
              if (selectedLayer === null || state.selectedLayer === undefined) {
                return null
              }
              if (!isModelDatasetLayer(selectedLayer)) {
                return null
              }
              const modelDatasetLayer = selectedLayer
              const layerStats = getLayerData(modelDatasetLayer)
              if (!isTileGridLoaded(layerStats)) {
                return null
              }
              return (
                <CopyLayerStyleControl
                  targetLayerId={state.selectedLayer}
                  targetGridType={layerStats.type}
                  entries={modelDatasetStyleEntries}
                  onApply={(style, source) => {
                    if (state.selectedLayer === undefined) return
                    const targetLayer = state.project.layers[state.selectedLayer]
                    if (
                      !isModelDatasetLayer(targetLayer) ||
                      source === undefined ||
                      layerStats.type !== "CategoricalTileGrid"
                    ) {
                      mutateLayer(state.selectedLayer, style)
                      return
                    }
                    const sourceStats = getLayerData(source.layer)
                    if (sourceStats.type !== "CategoricalTileGrid") {
                      mutateLayer(state.selectedLayer, style)
                      return
                    }
                    mutateLayer(
                      state.selectedLayer,
                      mergeCategoricalStyleOnCopy(
                        style,
                        source.layer,
                        labelEntriesFromStatsLabels(sourceStats.labels),
                        labelEntriesFromStatsLabels(layerStats.labels),
                        layerStats.max
                      )
                    )
                  }}
                />
              )
            })()}
            {
              selectedLayer?.type == "OverlayLayer" &&
              <OverlayLayerSettings
                layer={selectedLayer}
                mutate={
                  data => state.selectedLayer !== undefined &&
                    mutateLayer(state.selectedLayer, data)
                }
              />
            }
            {
              selectedLayer?.type == "NevoLayer" &&
              <NevoLayerSettings
                layer={selectedLayer}
                mutate={
                  data => state.selectedLayer !== undefined &&
                    mutateLayer(state.selectedLayer, data)
                }
              />
            }
            {
              (selectedLayer?.type == "ModelOutputLayer" || selectedLayer?.type == "DatasetLayer") &&
              <ModelOutputLayerSettings
                layer={selectedLayer}
                mutate={
                  data => state.selectedLayer !== undefined &&
                    mutateLayer(state.selectedLayer, data)
                }
                layerType={
                  getLayerData(selectedLayer).type
                }
                model={model}
                tileGrid={getTileGrid?.(selectedLayer) ?? null}
              />
            }
            {
              selectedLayer?.type == "CehLandCoverLayer" &&
              <CehLandCoverLayerSettings />
            }
            {
              selectedLayer?.type == "CropMapLayer" &&
              <CropMapLayerSettings layer={selectedLayer} />
            }
            {
              selectedLayer?.type == "AtiLayer" &&
              <ATILayerSettings layer={selectedLayer} />
            }
            {
              selectedLayer?.type == "GeoserverLayer" &&
              <GeoserverLayerSettings
                layer={selectedLayer}
                mutate={
                  data => state.selectedLayer !== undefined &&
                    mutateLayer(state.selectedLayer, data)
                }
              />
            }
            {
              selectedLayer?.type == "ShapeLayer" &&
              <ShapeLayerSettings
                layer={selectedLayer}
                mutate={
                  data => state.selectedLayer !== undefined &&
                    mutateLayer(state.selectedLayer, data)
                }
              />
            }
            {
              selectedLayer?.type == "KewLayer" &&
              <KewLayerSettings 
                layer={selectedLayer} 
                mutate={
                  data => state.selectedLayer !== undefined &&
                  mutateLayer(state.selectedLayer, data)
                } 
              />
            }
            {
              selectedLayer?.type == "KewPointLayer" &&
              <KewPointLayerSettings
                layer={selectedLayer}
                mutate={
                  data => state.selectedLayer !== undefined &&
                  mutateLayer(state.selectedLayer, data)
                }
              />
            }
            {
              selectedLayer?.type == "KewShapeLayer" &&
              <KewShapeLayerSettings
                layer={selectedLayer}
                mutate={
                  data => state.selectedLayer !== undefined &&
                  mutateLayer(state.selectedLayer, data)
                }
              />
            }
            {
              selectedLayer?.type == "ORValLayer" &&
              <>
                <img src={selectedLayer.style !== "" ? `https://landscapes.wearepal.ai/geoserver/wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=${selectedLayer.source}&STYLE=${selectedLayer.style}` : ""} alt="" />
              </>
            }
            {
              selectedLayer?.type == "IMDLayer" &&
              <IMDLayerSettings 
                layer={selectedLayer}
                mutate={
                  data => state.selectedLayer !== undefined &&
                    mutateLayer(state.selectedLayer, data)
                }
               />
            }
            {
              selectedLayer?.type == "WFSLayer" &&
              <WFSLayerSettings 
                layer={selectedLayer}
                mutate={
                  data => state.selectedLayer !== undefined &&
                    mutateLayer(state.selectedLayer, data)
                }
              />
            }
              </> :
              <em>No layer selected</em>
            }
            </div>
    )}
    {
      (selectedLayer?.type == "ModelOutputLayer" || selectedLayer?.type == "DatasetLayer") &&
      getLayerData(selectedLayer).type &&
        <div>
          <div className="d-flex layer-settings-footer-actions">
          {exportStatus.status === 'idle' ? (
            <>
              <button
                type="button"
                className="btn btn-outline-success rounded-0 border-left-0 border-bottom-0 flex-fill"
                style={{ minWidth: 0 }}
                onClick={() => saveLayerAsDataset(selectedLayer)}
              >
                Save
              </button>
              {getLayerData(selectedLayer).type === "CategoricalTileGrid" ? (
                <div className="btn-group flex-fill" style={{ minWidth: 0 }}>
                  <button
                    type="button"
                    className="btn btn-outline-success rounded-0 border-left-0 border-bottom-0 dropdown-toggle"
                    style={{ minWidth: 0, flex: 1 }}
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    Export
                  </button>
                  <div className="dropdown-menu dropdown-menu-right">
                    <button type="button" className="dropdown-item" onClick={() => exportLayerAsGeoTiff(selectedLayer)}>
                      GeoTIFF
                    </button>
                    <button type="button" className="dropdown-item" onClick={() => exportLayerAsShapefile(selectedLayer)}>
                      Shapefile
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline-success rounded-0 border-left-0 border-bottom-0 flex-fill"
                  style={{ minWidth: 0 }}
                  onClick={() => exportLayerAsGeoTiff(selectedLayer)}
                >
                  Export
                </button>
              )}
            </>
          ) : (
            <div className="p-2 border-top flex-grow-1" style={{ fontSize: '0.85em' }}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="fw-bold text-capitalize">{exportStatus.status}</span>
                <span>{exportStatus.progress}%</span>
              </div>
              <div className="progress mb-2" style={{ height: '4px' }}>
                <div
                  className={`progress-bar ${
                    exportStatus.status === 'error' ? 'bg-danger' :
                    exportStatus.status === 'completed' ? 'bg-success' : 'bg-primary'
                  }`}
                  style={{ width: `${exportStatus.progress}%` }}
                ></div>
              </div>
              <div className="text-muted small">
                {exportStatus.message}
                {exportStatus.error && (
                  <div className="text-danger mt-1">
                    Error: {exportStatus.error}
                  </div>
                )}
              </div>
            </div>
          )}
              {selectedLayer.type === "DatasetLayer" && (
              <button
                type="button"
                className="btn btn-outline-danger rounded-0 border-left-0 border-right-0 border-bottom-0 flex-fill"
                style={{ minWidth: 0 }}
                onClick={
                  () => state.selectedLayer !== undefined &&
                    deleteLayer(state.selectedLayer)
                }
              >
                Delete
              </button>
              )}
          </div>
        </div>
    }
    {
      selectedLayer?.type == "PaintbrushGeoJSONLayer" &&
      <div className="d-flex">
        <button
          type="button"
          className="btn btn-outline-success rounded-0 border-left-0 border-right-0 border-bottom-0 flex-fill"
          style={{ minWidth: 0 }}
          onClick={() =>
            window.open(`/paintbrush/${selectedLayer.annotationId}`, "_blank", "noopener,noreferrer")
          }
        >
          Edit in Paintbrush
        </button>
      </div>
    }
    {
      selectedLayer !== null &&
      selectedLayer.type !== "ModelOutputLayer" &&
      !(selectedLayer.type === "DatasetLayer" && getLayerData(selectedLayer).type) &&
      <button
      className="btn btn-outline-danger rounded-0 border-left-0 border-right-0 border-bottom-0"
      onClick={
        () => state.selectedLayer !== undefined &&
          deleteLayer(state.selectedLayer)
      }
    >
      Delete layer
    </button>
    }
    </>
    </div>
    )}
  </div>
}

interface CollapsedSidebarProps {
  show: () => void
  /** Narrow strip (e.g. embed): minimal width and padding */
  compact?: boolean
}
export const CollapsedSidebar = ({ show, compact }: CollapsedSidebarProps) =>
  compact ? (
    <div className="bg-light flex-shrink-0 border-left d-flex flex-column">
      <div
        className="border-top border-bottom d-flex align-items-center justify-content-center flex-grow-1"
        style={{ minWidth: "1.2rem", padding: "0.28rem 0.15rem" }}
      >
        <i className="fas fa-angle-double-left" style={{ cursor: "pointer", fontSize: "0.72rem" }} onClick={show} />
      </div>
    </div>
  ) : (
    <div className="bg-light">
      <div className="px-3 py-2 border-top border-bottom">
        <i className="fas fa-angle-double-left" style={{ cursor: "pointer" }} onClick={show} />
      </div>
    </div>
  )
